import React from 'react'
import { richTextRendererDlp } from '../rich-text'
import type { MediaGalleryItem } from '@ccpgames/eve-web-core'
import type { MediaBlockFieldsFragment } from '@/graphql/types'
import { hrefFromMediaBlockPage, PageReference } from '@/helpers/page-href'
import type { MediaGalleryFieldsFragment } from '@/graphql/types'
import { NextImageWrapper, type NextImageWrapperProps } from '@/components/shared/NextImageWrapper'

/**
 * Maps MediaGallery contentCollection items to MediaGalleryItem[] format
 * This function handles the transformation from Contentful/GraphQL data structure
 * to the format expected by DynamicMediaGallery components.
 *
 * Filters out items that don't have media, ensuring only valid MediaBlocks are included.
 *
 * @param contentCollection - The contentCollection from MediaGalleryFieldsFragment
 * @param locale - Current locale, used for resolving internal page links
 * @returns Array of MediaGalleryItem objects ready for DynamicMediaGallery
 */
interface MediaGalleryImageOptions {
  view?: 'grid' | 'slides' | 'rows' | 'quotes'
  sizes?: string
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down'
  fill?: boolean
  width?: number
  height?: number
  getItemSectionInspectorProps?: (
    mediaBlock: MediaBlockFieldsFragment,
  ) => MediaGalleryItem['sectionInspectorProps'] | undefined
}

const viewSizes = {
  grid: '(min-width: 992px) 33vw, (min-width: 768px) 50vw, 100vw',
  slides: '100vw',
  rows: '(min-width: 1024px) 50vw, 100vw',
  quotes: '300px',
}

function getSizes(options: MediaGalleryImageOptions) {
  return options.sizes ?? (options.view ? viewSizes[options.view] : '100vw')
}

function getImageStyle(options: MediaGalleryImageOptions) {
  return options.objectFit ? { objectFit: options.objectFit } : undefined
}

function resolveMediaBlockUrl(mediaBlock: MediaBlockFieldsFragment, locale?: string) {
  const internalHref = locale ? hrefFromMediaBlockPage(locale, mediaBlock.page as PageReference) : null
  const resolvedUrl = internalHref ?? mediaBlock.url ?? null
  const isExternal = !internalHref && Boolean(mediaBlock.url)

  return { resolvedUrl, isExternal }
}

function buildImageElement(
  mediaBlock: MediaBlockFieldsFragment,
  options: MediaGalleryImageOptions,
  sizes: string,
): React.ReactElement {
  const imageStyle = getImageStyle(options)
  const media = mediaBlock.media
  const width = options.width ?? media?.width ?? undefined
  const height = options.height ?? media?.height ?? undefined

  if (options.view === 'quotes') {
    return React.createElement('img', {
      src: media?.url ?? '',
      alt: media?.description ?? media?.title ?? '',
      style: imageStyle,
    })
  }

  const imageProps: NextImageWrapperProps = {
    fill: options.fill ?? true,
    sizes,
    style: imageStyle,
    src: media?.url ?? '',
    alt: media?.description ?? media?.title ?? '',
  }

  if (typeof width === 'number') imageProps.width = width
  if (typeof height === 'number') imageProps.height = height
  return React.createElement(NextImageWrapper, imageProps)
}

export function mapMediaBlocksToItems(
  contentCollection: MediaGalleryFieldsFragment['contentCollection'] | null | undefined,
  options: MediaGalleryImageOptions = {},
  locale?: string,
): MediaGalleryItem[] {
  if (!contentCollection?.items) {
    return []
  }

  const sizes = getSizes(options)
  const items: MediaGalleryItem[] = []

  for (const item of contentCollection.items) {
    // Skip null items
    if (!item) continue

    // Only process MediaBlock items
    if (item.__typename !== 'MediaBlock') continue

    // Only include items with media
    if (!item.media?.url) continue

    const mediaBlock = item as MediaBlockFieldsFragment
    const { resolvedUrl, isExternal } = resolveMediaBlockUrl(mediaBlock, locale)
    const image = buildImageElement(mediaBlock, options, sizes)

    items.push({
      id: item.sys.id,
      type: 'image',
      title: item.title ?? null,
      content: item.text?.json ? richTextRendererDlp(item.text) : undefined,
      image,
      url: resolvedUrl,
      external: isExternal,
      sectionInspectorProps: options.getItemSectionInspectorProps?.(mediaBlock),
    })
  }

  return items
}
